import type {
  MatchingResponse,
  RemedyOutcomeCode,
  RemedyOutcomeResponse,
} from "@ishvaram/api-client";
import { env } from "@/env";
import type { NextAction } from "@/lib/api/chat";
import type { AccountDecisionJourney } from "./account-journey-api";

export type { RemedyOutcomeCode, RemedyOutcomeResponse };
export {
  archiveDecisionJourney,
  checkInDecisionJourney,
  upsertDecisionJourney,
} from "./account-journey-api";
export type {
  AccountDecisionJourney,
  DecisionJourneyGoal,
  DecisionJourneyOutcome,
  DecisionJourneyType,
} from "./account-journey-api";

export interface AccountOrder {
  order_id: string;
  kind: "digital_report" | "physical" | "session" | "subscription";
  title: string;
  purchased_at: string;
  status: string;
  download_url: string | null;
  track_url: string | null;
  amount_inr: number | null;
  amount_minor?: number | null;
  currency?: string | null;
  // Paid report can be re-sent to the customer's email from the account hub.
  can_resend?: boolean;
  // False = paid report whose copy never reached the buyer (no send/download).
  delivered?: boolean;
  // Receipt-authoritative paid-report lifecycle from the account API.
  delivery_stage?: "paid" | "preparing" | "ready" | "delivered";
  generated_at?: string | null;
  delivered_at?: string | null;
  delivery_channel?: "email" | "whatsapp" | "download" | null;
}

export interface AccountMembership {
  active: boolean;
  plan_id: string | null;
  status: string | null;
  provider: string | null;
  current_period_end: string | null;
  subscription_id: string | null;
}

export interface AccountReading {
  reading_id: string;
  title: string;
  created_at: string;
  language: string | null;
  source: string | null;
  chat_url: string;
  assigned_jyotishi: string | null;
  conversation_turns: number;
  last_message: string | null;
  last_message_at: string | null;
  report_type: string | null;
  active_concern?: string | null;
  concern_id?: string | null;
  last_advice?: string | null;
  persona_id?: string | null;
}

export interface AccountJyotishPeriod {
  planet_id: string;
  name_en: string;
  name_hi: string;
}

export interface AccountJyotishTransit {
  planet_id: string;
  name_en: string;
  name_hi: string;
  rashi_en: string;
  rashi_hi: string;
  house_from_moon: number;
  is_retrograde: boolean;
  effect?: string;
  severity?: number;
  is_benefic?: boolean;
  description_en?: string;
  description_hi?: string;
}

export interface AccountJyotishSnapshot {
  reading_id: string;
  as_of: string;
  language: string;
  moon_rashi_en: string;
  moon_rashi_hi: string;
  mahadasha: AccountJyotishPeriod | null;
  antardasha: AccountJyotishPeriod | null;
  transits: AccountJyotishTransit[];
}

export interface AccountDailyGuidance {
  artifact_id: string;
  as_of: string;
  language: string;
  guidance_version: string;
  status: "ready" | "degraded";
  moon_rashi: string | null;
  moon_rashi_display: string | null;
  panchang: { paksha: string; tithi: string; nakshatra: string } | null;
  rashifal: string | null;
  mahadasha_lord: string | null;
  personal_focus: {
    rule_id: string;
    headline: string;
    body: string;
    action: string;
    source_label: string;
  } | null;
  degraded_sections: string[];
}

export type AccountRelationshipAction = NextAction;

export interface AccountRelationshipHome {
  current_reading_id: string;
  relationship_measurement_id: string;
  relationship_stage: string;
  state_version: number;
  active_concern: string | null;
  concern_id: string | null;
  remembered_opener: string | null;
  last_advice: string | null;
  today_guidance: AccountDailyGuidance | null;
  next_action: AccountRelationshipAction | null;
  pending_outcome: AccountRemedyFollowup | null;
}

export interface AccountRemedyFollowup {
  prescription_id: string;
  remedy_text: string;
  prescribed_at: string;
  due_at: string;
  message_status:
    | "queued"
    | "running"
    | "retry_wait"
    | "sent"
    | "dead_letter"
    | "manual_review"
    | "canceled";
  message_channel: "push" | "email" | "whatsapp" | null;
  sent_at: string | null;
  outcome_code: RemedyOutcomeCode | null;
  outcome_recorded_at: string | null;
}

export interface AccountRemedyPlan {
  prescription_id: string;
  reading_id: string;
  remedy_text: string;
  prescribed_at: string;
  remedy_kind: string;
  plan_template_key: string;
  plan_schema_version: string;
  outcome_code: RemedyOutcomeCode | null;
  completed_count: number;
  skipped_count: number;
  last_practice_date: string | null;
  title: string;
  cadence_label: string;
  completion_target: number;
  practice_enabled: boolean;
  steps: string[];
  source_label: string;
  source_note: string;
  safety_note: string;
  status: "active" | "completed";
}

export interface RemedyPracticeResponse {
  event_id: string;
  prescription_id: string;
  practice_date: string;
  action: "completed" | "skipped";
  recorded_at: string;
  deduped: boolean;
}

export type ChartRelationship =
  | "self"
  | "spouse"
  | "partner"
  | "parent"
  | "child"
  | "sibling"
  | "family"
  | "friend"
  | "other";

export interface AccountChartSubject {
  subject_id: string;
  reading_id: string;
  relationship_to_owner: ChartRelationship;
  display_name: string;
  birth_date: string;
  birth_place: string;
  language: string | null;
  moon_rashi: string | null;
  collection_state_version: number;
}

export interface AccountOrdersResponse {
  email: string;
  phone?: string;
  contact?: string;
  contact_kind?: "email" | "phone" | "auth";
  orders: AccountOrder[];
  membership: AccountMembership | null;
  membership_enrollment_open?: boolean;
  readings: AccountReading[];
  jyotishi_snapshot?: AccountJyotishSnapshot | null;
  relationship_home?: AccountRelationshipHome | null;
  remedy_followups?: AccountRemedyFollowup[];
  chart_subjects?: AccountChartSubject[];
  decision_journeys?: AccountDecisionJourney[];
  remedy_plans?: AccountRemedyPlan[];
}

export interface CancelMembershipResponse {
  success: boolean;
  membership: AccountMembership & { cancel_at_cycle_end: boolean };
}

export interface ResendOrderResponse {
  ok: boolean;
  deduped: boolean;
  channel: string;
  masked_email: string;
  masked_phone: string;
  masked_destination: string;
  message: string;
}

export async function requestAccountLink(contact: string): Promise<void> {
  const value = contact.trim();
  const body = value.includes("@") ? { email: value } : { phone: value };
  await fetch(`${env.NEXT_PUBLIC_API_URL}/account/request-link`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

export async function accountSessionFromReport(reportToken: string): Promise<string> {
  const res = await fetch(
    `${env.NEXT_PUBLIC_API_URL}/account/from-report/${encodeURIComponent(reportToken)}`,
    { method: "POST" }
  );
  if (!res.ok) throw new Error("This private report link can no longer open the account.");
  const body = (await res.json()) as { token?: string };
  if (!body.token) throw new Error("Account access token was not returned.");
  return body.token;
}

export async function accountSessionFromAuth(
  accessToken: string
): Promise<string | "unauthorized"> {
  const res = await fetch(`${env.NEXT_PUBLIC_API_URL}/account/from-auth`, {
    method: "POST",
    headers: { Authorization: `Bearer ${accessToken}` },
  });
  if (res.status === 401) return "unauthorized";
  if (!res.ok) throw new Error(`Could not open signed-in account (HTTP ${res.status})`);
  const body = (await res.json()) as { token?: string };
  if (!body.token) throw new Error("Signed-in account token was not returned.");
  return body.token;
}

export async function fetchAccountOrders(
  token: string
): Promise<AccountOrdersResponse | "unauthorized"> {
  const res = await fetch(`${env.NEXT_PUBLIC_API_URL}/account/orders`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (res.status === 401) return "unauthorized";
  if (!res.ok) throw new Error(`Failed to load orders (HTTP ${res.status})`);
  return (await res.json()) as AccountOrdersResponse;
}

export async function cancelMembership(token: string): Promise<CancelMembershipResponse> {
  const res = await fetch(`${env.NEXT_PUBLIC_API_URL}/account/membership/cancel`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ cancel_at_cycle_end: true }),
  });
  if (res.status === 401) throw new Error("Your account link has expired. Please sign in again.");
  if (!res.ok) throw new Error(`Failed to cancel membership (HTTP ${res.status})`);
  return (await res.json()) as CancelMembershipResponse;
}

export async function resendOrder(token: string, orderId: string): Promise<ResendOrderResponse> {
  const res = await fetch(
    `${env.NEXT_PUBLIC_API_URL}/account/orders/${encodeURIComponent(orderId)}/resend`,
    {
      method: "POST",
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  if (res.status === 401) throw new Error("Your account link has expired. Please sign in again.");
  if (!res.ok) throw new Error(`Could not send your reading (HTTP ${res.status})`);
  return (await res.json()) as ResendOrderResponse;
}

export async function recordRemedyOutcome(
  token: string,
  prescriptionId: string,
  outcomeCode: RemedyOutcomeCode,
  idempotencyKey: string
): Promise<RemedyOutcomeResponse> {
  const res = await fetch(
    `${env.NEXT_PUBLIC_API_URL}/account/remedies/${encodeURIComponent(prescriptionId)}/outcome`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        outcome_code: outcomeCode,
        idempotency_key: idempotencyKey,
      }),
    }
  );
  if (res.status === 401 || res.status === 403) {
    throw new Error("Sign in with your verified Ishvaram account to save this upay result.");
  }
  if (res.status === 404) throw new Error("This upay follow-up is no longer available.");
  if (res.status === 409) throw new Error("Please choose the result again and retry.");
  if (!res.ok) throw new Error(`Could not save your upay result (HTTP ${res.status})`);
  return (await res.json()) as RemedyOutcomeResponse;
}

export async function recordRemedyPractice(
  token: string,
  prescriptionId: string,
  practiceDate: string,
  action: "completed" | "skipped",
  idempotencyKey: string
): Promise<RemedyPracticeResponse> {
  const res = await fetch(
    `${env.NEXT_PUBLIC_API_URL}/account/remedies/${encodeURIComponent(prescriptionId)}/practice`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        practice_date: practiceDate,
        action,
        idempotency_key: idempotencyKey,
      }),
    }
  );
  if (res.status === 401 || res.status === 403) {
    throw new Error("Sign in with your verified Ishvaram account to update this upay plan.");
  }
  if (res.status === 404) throw new Error("This upay plan is no longer available.");
  if (res.status === 409) throw new Error("Please retry the same practice action.");
  if (!res.ok) throw new Error(`Could not update the upay plan (HTTP ${res.status})`);
  return (await res.json()) as RemedyPracticeResponse;
}

export async function attachChartSubject(
  token: string,
  readingId: string,
  relationshipToOwner: ChartRelationship
): Promise<void> {
  const res = await fetch(`${env.NEXT_PUBLIC_API_URL}/account/chart-subjects`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      reading_id: readingId,
      relationship_to_owner: relationshipToOwner,
    }),
  });
  if (res.status === 401 || res.status === 403) {
    throw new Error("Sign in with your verified Ishvaram account to save this chart.");
  }
  if (res.status === 404) throw new Error("That private kundali could not be found.");
  if (res.status === 409) {
    throw new Error("That chart is already claimed by another verified account.");
  }
  if (!res.ok) throw new Error(`Could not save chart (HTTP ${res.status})`);
}

export async function archiveChartSubject(token: string, subjectId: string): Promise<void> {
  const res = await fetch(
    `${env.NEXT_PUBLIC_API_URL}/account/chart-subjects/${encodeURIComponent(subjectId)}`,
    {
      method: "DELETE",
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  if (res.status === 409) throw new Error("Your own chart stays as the collection anchor.");
  if (res.status === 404) throw new Error("That saved chart is no longer available.");
  if (!res.ok) throw new Error(`Could not remove chart (HTTP ${res.status})`);
}

export async function matchChartSubjects(
  token: string,
  subjectAId: string,
  subjectBId: string,
  language: "en" | "hi" = "en"
): Promise<MatchingResponse> {
  const res = await fetch(`${env.NEXT_PUBLIC_API_URL}/account/matching`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      subject_a_id: subjectAId,
      subject_b_id: subjectBId,
      language,
    }),
  });
  if (res.status === 404) throw new Error("Choose two available saved charts.");
  if (!res.ok) throw new Error(`Could not compare charts (HTTP ${res.status})`);
  return (await res.json()) as MatchingResponse;
}
